import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { removeModule, MODULE_TYPES } from '../../store/dashboard/dashboardSlice';
import { desktopTheme as t } from '../../styles/desktopTheme';

// Import module content components
import TasksModule from './modules/TasksModule';
import ShowsModule from './modules/ShowsModule';
import ScheduleModule from './modules/ScheduleModule';
import NotesModule from './modules/NotesModule';
import DemosModule from './modules/DemosModule';
import ReleasesModule from './modules/ReleasesModule';
import TicketCountModule from './modules/TicketCountModule';
import TeamModule from './modules/TeamModule';
import AnalyticsModule from './modules/AnalyticsModule';
import WorksModule from './modules/WorksModule';

const ModuleContainer = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.borderSubtle};
  border-radius: 14px;
  min-height: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: ${t.shadowCard};

  &:hover {
    background: ${t.surfaceHover};
    border-color: ${t.borderStrong};
  }

  ${props => props.editMode && `
    cursor: grab;
    
    &:hover {
      border-color: rgba(255, 68, 68, 0.5);
      box-shadow: 0 0 0 2px rgba(255, 68, 68, 0.2);
    }
  `}
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px 20px;
`;

const ModuleTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${t.textPrimary};
  margin: 0;
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 68, 68, 0.2);
  border: none;
  color: ${t.accent};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ModuleContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(255, 35, 0, 0.35);
  }
`;

const ModuleContent = styled.div`
  flex: 1;
  padding: 8px 20px 20px 20px;
  overflow-y: auto;
`;

// Map module types to their content components
const MODULE_COMPONENTS = {
  tasks: TasksModule,
  shows: ShowsModule,
  schedule: ScheduleModule,
  notes: NotesModule,
  demos: DemosModule,
  releases: ReleasesModule,
  ticketCount: TicketCountModule,
  team: TeamModule,
  analytics: AnalyticsModule,
  works: WorksModule,
};

export default function DashboardModule({ module, editMode }) {
  const dispatch = useDispatch();
  const { id, type } = module;
  
  const moduleInfo = MODULE_TYPES[type] || { label: type };
  const ContentComponent = MODULE_COMPONENTS[type];

  const handleRemove = (e) => {
    e.stopPropagation();
    dispatch(removeModule({ id }));
  };

  return (
    <ModuleContainer editMode={editMode}>
      <ModuleHeader>
        <ModuleTitle>{moduleInfo.label}</ModuleTitle>
        {editMode ? <RemoveButton onClick={handleRemove}>×</RemoveButton> : null}
      </ModuleHeader>
      <ModuleContent>
        {ContentComponent ? (
          <ContentComponent />
        ) : (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
            Module: {type}
          </div>
        )}
      </ModuleContent>
    </ModuleContainer>
  );
}

